import React from "react";
import type { IExamReport } from "@/apis/exam_papers/getExamReport";
import type { ICategory } from "@/apis/categories/types";
import ProgressBar from "./ProgressBar";
import { QuestionTable } from "@/components/exam/QuestionTable";
import { CategoryStatsTable } from "@/components/exam/CategoryStatsTable";
import { Badge } from "@/components/badge";
import { FEATURES } from "@/config/features";

interface SATScoreReportProps {
  examReport: IExamReport;
  categories?: ICategory[];
}

const SATScoreReport: React.FC<SATScoreReportProps> = ({
  examReport,
  categories,
}) => {
  const isDevelopment = false; //process.env.NODE_ENV === "development";

  // Helper function to get section/module label based on exam type
  const getSectionLabel = (isSAT: boolean = false) => {
    return isSAT ? "Module" : "Section";
  };

  const calculateDomainPercentage = (
    domainQuestions: number,
    allDomains: Array<{ total_questions?: number }>
  ) => {
    const totalQuestions = allDomains.reduce(
      (sum, d) => sum + (d.total_questions || 0),
      0
    );
    return Math.round((domainQuestions / (totalQuestions || 1)) * 100);
  };

  const { subjects } = examReport;
  if (!subjects || subjects.length === 0) {
    return <div>No subject details found</div>;
  }

  // Get subject reports by subject type
  const readingWritingReport = subjects.find(
    (s: any) => s.subject === "reading_writing"
  );
  const mathReport = subjects.find((s: any) => s.subject === "math");

  // Check if it's SAT exam
  const isSAT = examReport?.exam_set?.exam_type === "SAT";

  return (
    <>
      {/* PAGE 1: SAT Practice Score Report */}
      <div
        className={`page w-[210mm] h-[297mm] px-8 py-6 text-black font-sans text-base bg-white ${
          isDevelopment ? "border-2 border-red-500" : ""
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-2 h-[100px]">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold tracking-tight ">
              S&S ACADEMY
            </span>
          </div>
          <div className="text-right ">
            <div className="inline-block rounded-lg p-2 text-left">
              <div className="text-lg mb-1 leading-none">
                <span className="inline-block w-24">Name:</span>
                <span className="font-bold">{examReport?.user?.name}</span>
              </div>
              {examReport?.group?.name && (
                <div className="text-base mb-1 leading-none">
                  <span className="inline-block w-24">Class:</span>
                  <span className="font-bold">{examReport?.group?.name}</span>
                </div>
              )}
              {examReport?.user?.school && (
                <div className="text-base mb-1 leading-none">
                  <span className="inline-block w-24">School:</span>
                  <span className="font-bold">{examReport?.user?.school}</span>
                </div>
              )}
              {examReport?.user?.grade && (
                <div className="text-base mb-1 leading-none">
                  <span className="inline-block w-24">Grade:</span>
                  <span className="font-bold">{examReport?.user?.grade}</span>
                </div>
              )}
              <div className="text-base mb-1 leading-none">
                <span className="inline-block w-24">Email:</span>
                <span className="font-bold">{examReport?.user?.email}</span>
              </div>
              <div className="text-base leading-none">
                <span className="inline-block w-24">Tested on:</span>
                <span className="font-bold">
                  {new Date(examReport?.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-extrabold mt-16 mb-6">
          Your SAT Score Report
        </h1>

        {/* Card Section */}
        <div className="rounded-xl border border-gray-300 bg-white shadow-sm mt-2 mb-4 p-0 overflow-hidden">
          <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 bg-gray-200">
            <div className="text-xl font-bold">
              {examReport?.exam_set?.title}
            </div>
            <div className="text-base text-gray-700">
              {new Date(examReport?.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="grid grid-cols-20 gap-0 divide-x divide-gray-200">
            {/* Total Score */}
            <div className="col-span-5 p-4 px-6 flex flex-col items-start">
              <div className="text-md font-extrabold">TOTAL SCORE</div>
              <div className="text-6xl font-bold leading-none mb-2">
                {examReport?.total_score}
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Score Range
                <br /> 400-1600
              </div>
              <div className="text-sm text-gray-800 font-bold mt-2">
                Score Range:
              </div>
              <div className="text-sm text-gray-600">
                This range reflects the minimum and maximum total score you can
                get with this assessment.
              </div>
            </div>
            {/* Section Scores */}
            <div className="col-span-5 p-4 flex flex-col items-start">
              <div className="text-md font-extrabold mb-3">SECTION SCORES</div>
              {/* Reading and Writing */}
              <div className="w-full flex-1">
                <div className="font-bold text-sm mb-3">
                  Reading and Writing
                </div>
                <div className="font-bold text-5xl leading-none">
                  {readingWritingReport?.total_score || 0}
                </div>
              </div>
              <hr className="border-dashed border-gray-300 w-full my-2" />
              {/* Math */}
              <div className="w-full items-center justify-between flex-1">
                <div className="font-bold text-sm mb-3">Math</div>
                <div className="font-bold text-5xl leading-none">
                  {mathReport?.total_score || 0}
                </div>
              </div>
            </div>
            {/* Score Details */}
            <div className="col-span-10 py-4 px-4 flex flex-col items-start">
              <div className="text-md font-extrabold mb-3">SCORE DETAILS</div>
              <div className="flex flex-col w-full text-sm h-full">
                <div className="flex-1">
                  <div className="grid grid-cols-5 gap-2">
                    {/* Combined Results */}
                    <div className="col-span-2">
                      <p className="text-sm font-bold mb-2">Total</p>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <p className="text-3xl font-bold">
                            {(readingWritingReport?.sections[0]?.questions?.filter(
                              (q) => q.is_correct
                            ).length || 0) +
                              (readingWritingReport?.sections[1]?.questions?.filter(
                                (q) => q.is_correct
                              ).length || 0)}
                          </p>
                          <div className="border-l border-gray-300 h-6 mx-0"></div>
                          <p className="text-xs leading-none font-bold text-start">
                            correct
                            <br />
                            Answers
                          </p>
                        </div>
                        <div className="mt-2">
                          <p className="text-xs leading-none">
                            Total:{" "}
                            <span className="font-normal">
                              {(readingWritingReport?.sections[0]?.questions
                                ?.length || 0) +
                                (readingWritingReport?.sections[1]?.questions
                                  ?.length || 0)}
                            </span>
                          </p>
                          <p className="text-xs leading-none">
                            Incorrect:{" "}
                            <span className="font-normal">
                              {(readingWritingReport?.sections[0]?.questions?.filter(
                                (q) => !q.is_correct
                              ).length || 0) +
                                (readingWritingReport?.sections[1]?.questions?.filter(
                                  (q) => !q.is_correct
                                ).length || 0)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Individual Sections */}
                    <div className="flex flex-col gap-2 col-span-3">
                      {/* Section 1 */}
                      <div>
                        <p className="text-sm font-bold mb-1">
                          {getSectionLabel(isSAT)} 1
                        </p>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <p className="text-3xl font-bold">
                              {readingWritingReport?.sections[0]?.questions?.filter(
                                (q) => q.is_correct
                              ).length || 0}
                            </p>
                            <div className="border-l border-gray-300 h-6"></div>
                            <p className="text-xs leading-none font-bold text-start">
                              correct
                              <br />
                              Answers
                            </p>
                          </div>
                          <div className="text-left">
                            <p className="text-xs leading-none">
                              Total:{" "}
                              <span className="font-normal">
                                {readingWritingReport?.sections[0]?.questions
                                  ?.length || 0}
                              </span>
                            </p>
                            <p className="text-xs leading-none">
                              Incorrect:{" "}
                              <span className="font-normal">
                                {readingWritingReport?.sections[0]?.questions?.filter(
                                  (q) => !q.is_correct
                                ).length || 0}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* Section 2 */}
                      <div>
                        <p className="text-sm font-bold mb-1">
                          {getSectionLabel(isSAT)} 2
                        </p>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <p className="text-3xl font-bold">
                              {readingWritingReport?.sections[1]?.questions?.filter(
                                (q) => q.is_correct
                              ).length || 0}
                            </p>
                            <div className="border-l border-gray-300 h-6"></div>
                            <p className="text-xs leading-none font-bold text-start">
                              correct
                              <br />
                              Answers
                            </p>
                          </div>
                          <div className="text-left">
                            <p className="text-xs leading-none">
                              Total:{" "}
                              <span className="font-normal">
                                {readingWritingReport?.sections[1]?.questions
                                  ?.length || 0}
                              </span>
                            </p>
                            <p className="text-xs leading-none">
                              Incorrect:{" "}
                              <span className="font-normal">
                                {readingWritingReport?.sections[1]?.questions?.filter(
                                  (q) => !q.is_correct
                                ).length || 0}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <hr className="border-dashed border-gray-300 w-full my-4" />

                <div className="flex-1">
                  <div className="grid grid-cols-5 gap-2">
                    {/* Combined Results */}
                    <div className="col-span-2">
                      <p className="text-sm font-bold mb-2">Total</p>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <p className="text-3xl font-bold">
                            {(mathReport?.sections[0]?.questions?.filter(
                              (q) => q.is_correct
                            ).length || 0) +
                              (mathReport?.sections[1]?.questions?.filter(
                                (q) => q.is_correct
                              ).length || 0)}
                          </p>
                          <div className="border-l border-gray-300 h-6 mx-0"></div>
                          <p className="text-xs leading-none font-bold text-start">
                            correct
                            <br />
                            Answers
                          </p>
                        </div>
                        <div className="mt-2">
                          <p className="text-xs leading-none">
                            Total:{" "}
                            <span className="font-normal">
                              {(mathReport?.sections[0]?.questions?.length ||
                                0) +
                                (mathReport?.sections[1]?.questions?.length ||
                                  0)}
                            </span>
                          </p>
                          <p className="text-xs leading-none">
                            Incorrect:{" "}
                            <span className="font-normal">
                              {(mathReport?.sections[0]?.questions?.filter(
                                (q) => !q.is_correct
                              ).length || 0) +
                                (mathReport?.sections[1]?.questions?.filter(
                                  (q) => !q.is_correct
                                ).length || 0)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Individual Sections */}
                    <div className="flex flex-col gap-2 col-span-3">
                      {/* Section 1 */}
                      <div>
                        <p className="text-sm font-bold mb-1">
                          {getSectionLabel(isSAT)} 1
                        </p>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <p className="text-3xl font-bold">
                              {mathReport?.sections[0]?.questions?.filter(
                                (q) => q.is_correct
                              ).length || 0}
                            </p>
                            <div className="border-l border-gray-300 h-6"></div>
                            <p className="text-xs leading-none font-bold text-start">
                              correct
                              <br />
                              Answers
                            </p>
                          </div>
                          <div className="text-left">
                            <p className="text-xs leading-none">
                              Total:{" "}
                              <span className="font-normal">
                                {mathReport?.sections[0]?.questions?.length ||
                                  0}
                              </span>
                            </p>
                            <p className="text-xs leading-none">
                              Incorrect:{" "}
                              <span className="font-normal">
                                {mathReport?.sections[0]?.questions?.filter(
                                  (q) => !q.is_correct
                                ).length || 0}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* Section 2 */}
                      <div>
                        <p className="text-sm font-bold mb-1">
                          {getSectionLabel(isSAT)} 2
                        </p>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <p className="text-3xl font-bold">
                              {mathReport?.sections[1]?.questions?.filter(
                                (q) => q.is_correct
                              ).length || 0}
                            </p>
                            <div className="border-l border-gray-300 h-6"></div>
                            <p className="text-xs leading-none font-bold text-start">
                              correct
                              <br />
                              Answers
                            </p>
                          </div>
                          <div className="text-left">
                            <p className="text-xs leading-none">
                              Total:{" "}
                              <span className="font-normal">
                                {mathReport?.sections[1]?.questions?.length ||
                                  0}
                              </span>
                            </p>
                            <p className="text-xs leading-none">
                              Incorrect:{" "}
                              <span className="font-normal">
                                {mathReport?.sections[1]?.questions?.filter(
                                  (q) => !q.is_correct
                                ).length || 0}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Knowledge and Skills */}
        <div className="mt-8">
          <div className="font-bold text-xl mb-1">KNOWLEDGE AND SKILLS</div>
          <div className="text-sm text-gray-700 mb-6">
            View your performance across the content domains measured on the SAT
          </div>
          <div className="grid grid-cols-2">
            {/* Reading and Writing */}
            {readingWritingReport && (
              <div>
                <div className="flex items-center gap-3">
                  <div className="text-black font-semibold text-lg">
                    Reading and Writing
                  </div>
                  {FEATURES.SHOW_REPORT_DIFFICULTY_LEVEL &&
                    readingWritingReport.difficulty && (
                      <Badge color="zinc">
                        Level {readingWritingReport.difficulty}
                      </Badge>
                    )}
                </div>
                {readingWritingReport?.domains?.map((domain) => (
                  <div key={domain?.id}>
                    <div className="mb-2 text-sm text-gray-700 mt-2">
                      <p className="font-bold">{domain.name}</p>
                      <p className="text-gray-600">
                        (
                        {calculateDomainPercentage(
                          domain?.total_questions || 0,
                          readingWritingReport?.domains
                        )}
                        % of test section, {domain?.total_questions || 0}{" "}
                        questions)
                      </p>
                    </div>
                    <ProgressBar percentage={(domain?.accuracy || 0) * 100} />
                  </div>
                ))}
              </div>
            )}
            {/* Math */}
            {mathReport && (
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="font-semibold text-lg">Math</div>
                  {FEATURES.SHOW_REPORT_DIFFICULTY_LEVEL &&
                    mathReport.difficulty && (
                      <Badge color="zinc">Level {mathReport.difficulty}</Badge>
                    )}
                </div>
                {mathReport?.domains?.map((domain) => (
                  <div key={domain.id}>
                    <div className="mb-2 text-sm text-gray-700 mt-2">
                      <p className="font-bold">{domain.name}</p>
                      <p className="text-gray-600">
                        (
                        {calculateDomainPercentage(
                          domain?.total_questions || 0,
                          mathReport?.domains
                        )}
                        % of test section, {domain?.total_questions || 0}{" "}
                        questions)
                      </p>
                    </div>
                    <ProgressBar percentage={(domain?.accuracy || 0) * 100} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PAGE 2: Detailed Question Breakdown */}
      <div
        className={`page w-[210mm] h-[297mm] px-10 text-black font-sans text-sm bg-white ${
          isDevelopment ? "border-2 border-red-500" : ""
        }`}
        style={{ pageBreakBefore: "always" }}
      >
        {/* 2x2 그리드 테이블 */}
        {readingWritingReport && (
          <div className="grid grid-cols-2 grid-rows-1 gap-4 ">
            {/* Reading & Writing Section 1 */}
            <div>
              <QuestionTable
                title={`Reading & Writing ${getSectionLabel(isSAT)} 1`}
                questions={readingWritingReport?.sections[0]?.questions}
                showAccuracy={true}
              />
            </div>

            {/* Reading & Writing Section 2 */}
            <div>
              <QuestionTable
                title={`Reading & Writing ${getSectionLabel(isSAT)} 2`}
                questions={readingWritingReport?.sections[1]?.questions}
                showAccuracy={true}
              />
            </div>
          </div>
        )}

        {/* Reading & Writing Category Stats */}
        {readingWritingReport && (
          <div className="mt-4">
            <CategoryStatsTable
              title="QUESTION COUNTS BY DETAILED CATEGORIES"
              examType={examReport?.exam_set?.exam_type || "SAT"}
              subject={readingWritingReport?.subject}
              categoryStats={readingWritingReport?.category_stats}
              recentExamsStats={readingWritingReport?.recent_exams_stats}
            />
          </div>
        )}

        {mathReport && (
          <div className="grid grid-cols-2 grid-rows-1 gap-4 mt-6">
            {/* Math Section 1 */}
            <div>
              <QuestionTable
                title={`Math ${getSectionLabel(isSAT)} 1`}
                questions={mathReport?.sections[0]?.questions}
                showAccuracy={true}
              />
            </div>

            {/* Math Section 2 */}
            <div>
              <QuestionTable
                title={`Math ${getSectionLabel(isSAT)} 2`}
                questions={mathReport?.sections[1]?.questions}
                showAccuracy={true}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SATScoreReport; 